/**
 * Canonical display name for a customer.
 *
 * If the customer has a company name set, that's the display name regardless
 * of customer type — companies are the entity being billed and that's what
 * dispatchers, bookers, and invoices need to see. The person on the record
 * (first_name + last_name) becomes the primary contact, not the customer identity.
 *
 * Falls back to the person's name when no company is set (true private
 * customers). Falls back to email if neither is set (rare edge case from
 * incomplete data).
 *
 * Use this everywhere a customer needs to be displayed: list rows, detail
 * page headers, booking attributions, emails, dropdowns, search results.
 */
export function customerDisplayName(customer) {
    if (!customer) return '—';
    if (customer.company_name && customer.company_name.trim() !== '') {
        return customer.company_name;
    }
    const fullName = [customer.first_name, customer.last_name]
        .filter(Boolean)
        .join(' ')
        .trim();
    if (fullName) return fullName;
    return customer.email || '—';
}

/**
 * Companion helper: returns the contact person string for display alongside
 * the customer name when the customer is a company. Returns null when there's
 * no useful contact info to show (avoids "Acme Corp · —" rendering).
 */
export function customerContactPerson(customer) {
    if (!customer) return null;
    if (!customer.company_name) return null; // person IS the customer, no need to show separately
    const fullName = [customer.first_name, customer.last_name]
        .filter(Boolean)
        .join(' ')
        .trim();
    return fullName || null;
}
